import { Box, Tab, Tabs, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { getAllTestDataFiles } from "src/clients/TestDataService";
import { login, selectUser } from "src/redux/slices/userSlice";
import { TOAST_MESSAGE } from "../constants/Constants";
import { TestDataContext } from "../context/Context";
import NotifyError from "../notifications/NotifyError";
import TestDataTabSubComponent from "./TestDataTabSubComponent";

interface ApiResponse {
  [outerKey1: string]: {
    [outerKey2: string]: {
      [innerKey: string]: any;
    };
  };
}

const TestDataTabComponent = () => {
  const [tabValue, setTabValue] = React.useState("1");
  const [initialTestData, setInitialTestData] = useState<
    Map<string, Map<string, Map<string, any>>>
  >(new Map<string, Map<string, Map<string, any>>>());
  const [mode, setMode] = useState<string | null>(null);
  const user = useSelector(selectUser);
  const dispatch = useDispatch();

  useEffect(() => {
    fetchTestData();
  }, []);

  useEffect(() => {
    setInitialTestData(new Map<string, Map<string, Map<string, any>>>());
    fetchTestData();
  }, [user.productCode, user.selectedClient]);

  const fetchTestData = async () => {
    dispatch(
      login({
        userDetailsId: user.userDetailsId,
        userId: user.userId,
        password: user.password,
        email: user.email,
        role: user.role,
        permissionsList: user.permissionsList,
        productCode: user.productCode,
        firstName: user.firstName,
        lastName: user.lastName,
        products: user.products,
        filesStatus: true,
        clientId: user.clientId,
        clientCode: user.clientCode,
        selectedClient: user.selectedClient,
        auth: user.auth,
      })
    );
    const testDataRes = await getAllTestDataFiles(
      user.auth,
      user.productCode,
      user.selectedClient
    );
    if (testDataRes.ok) {
      const data: ApiResponse = await testDataRes.json();
      const convertedData = new Map<string, Map<string, Map<string, any>>>(
        Object.entries(data).map(([outerKey, innerMap]) => [
          outerKey,
          new Map<string, Map<string, any>>(
            Object.entries(innerMap).map(([innerKey, attributes]) => {
              return [
                innerKey,
                new Map<string, any>(Object.entries(attributes)),
              ];
            })
          ),
        ])
      );
      setInitialTestData(convertedData);
    } else if (testDataRes.status === 408) {
      setInitialTestData(new Map());
      toast.error(
        <NotifyError header={"Error"}>
          <Typography variant="body2">
            {TOAST_MESSAGE.FAILED_TO_CONNECT}
          </Typography>
        </NotifyError>,
        {
          icon: false,
          closeButton: false,
        }
      );
    } else {
      setInitialTestData(new Map());
      toast.error(
        <NotifyError header={"Error"}>
          <Typography variant="body2">
            {TOAST_MESSAGE.INTERNAL_SERVER_ERROR}
          </Typography>
        </NotifyError>,
        {
          icon: false,
          closeButton: false,
        }
      );
    }
    dispatch(
      login({
        userDetailsId: user.userDetailsId,
        userId: user.userId,
        password: user.password,
        email: user.email,
        role: user.role,
        permissionsList: user.permissionsList,
        productCode: user.productCode,
        firstName: user.firstName,
        lastName: user.lastName,
        products: user.products,
        filesStatus: false,
        clientId: user.clientId,
        clientCode: user.clientCode,
        selectedClient: user.selectedClient,
        auth: user.auth,
      })
    );
  };

  const handleOnSwitchTab = (newValue: string) => {
    setTabValue(newValue);
  };

  const handleOnTabChange = (event: React.SyntheticEvent, newValue: string) => {
    handleOnSwitchTab(newValue);
  };

  const handleOnSetMode = (mode: string | null) => {
    setMode(mode);
  };

  return (
    <Box sx={{ width: "100%" }}>
      <TestDataContext.Provider value={{ initialTestData, setInitialTestData }}>
        {initialTestData && !mode && (
          <Tabs
            value={tabValue}
            onChange={handleOnTabChange}
            centered
          >
            <Tab
              value="1"
              label="Default Test Data"
            />
            <Tab
              value="2"
              label="User Test Data"
            />
          </Tabs>
        )}
        {tabValue === "1" && (
          <TestDataTabSubComponent
            isDefault={true}
            mode={mode}
            fetchTestData={fetchTestData}
            handleOnSetMode={handleOnSetMode}
            handleOnSwitchTab={handleOnSwitchTab}
          />
        )}
        {tabValue === "2" && (
          <TestDataTabSubComponent
            isDefault={false}
            mode={mode}
            fetchTestData={fetchTestData}
            handleOnSetMode={handleOnSetMode}
            handleOnSwitchTab={handleOnSwitchTab}
          />
        )}
      </TestDataContext.Provider>
    </Box>
  );
};

export default TestDataTabComponent;
